/**
 * Syif dan Z-report.
 *
 * Fungsi di sini **tulen**: masuk senarai jualan + bayaran, keluar ringkasan. Pengiraan tunai
 * ialah bahagian yang paling mudah salah dalam POS, jadi ia diuji secara berasingan daripada UI.
 *
 * Peraturan wang tunai:
 *   tunai bersih = bayaran tunai − baki dipulangkan
 *   sepatutnya    = tunai awal + tunai bersih
 *   beza          = tunai dikira − tunai sepatutnya
 */

import type { PaymentMethod, PaymentRecord, SaleRecord } from './types.js';

export interface ShiftRecord {
  id: string;
  opened_at: string;
  closed_at: string | null;
  opening_cash_sen: number;
  counted_cash_sen: number | null;
  expected_cash_sen: number | null;
  variance_sen: number | null;
  sales_count: number;
  sales_total_sen: number;
  payment_breakdown: Record<string, number>;
  status: 'open' | 'closed';
  /**
   * Siapa yang membuka syif.
   *
   * Syif ialah tempoh tanggungjawab SATU orang ke atas laci wang. Tanpa medan ini, Z-report tidak
   * boleh dikaitkan dengan sesiapa, dan "tukar kasir" akan menukar nama di tengah syif yang sama.
   */
  cashier_user_id?: string | null;
  cashier_name?: string | null;
  /**
   * Bila syif ini diterima pelayan.
   *
   * `null` bermakna ia masih menunggu penghantaran. Syif TIDAK disekat oleh rangkaian: kedai mesti
   * boleh menutup syif walaupun offline, dan ia dihantar kemudian.
   */
  uploaded_at?: string | null;
}

export interface ShiftSummary {
  sales_count: number;
  sales_total_sen: number;
  payment_breakdown: Record<string, number>;
  /** Tunai bersih dalam laci daripada jualan (bayaran tunai − baki). */
  cash_net_sen: number;
  voided_count: number;
  /**
   * Jumlah diskaun dalam syif.
   *
   * Diskaun mengurangkan hasil, bukan kos. Tanpa angka ini pada Z-report, pemilik melihat jualan
   * bersih sahaja dan tidak tahu berapa banyak margin yang diberi pada hari itu.
   */
  discount_sen: number;
}

export interface SaleWithPayments {
  sale: SaleRecord;
  payments: readonly PaymentRecord[];
}

/** Ringkaskan jualan dalam satu syif. */
export function summarizeShift(entries: readonly SaleWithPayments[]): ShiftSummary {
  const paymentBreakdown: Record<string, number> = {};
  let salesCount = 0;
  let salesTotal = 0;
  let cashNet = 0;
  let voided = 0;
  let discount = 0;

  for (const entry of entries) {
    if (entry.sale.voided_at !== undefined && entry.sale.voided_at !== null) {
      voided += 1;
      continue;
    }

    salesCount += 1;
    salesTotal += entry.sale.total_sen;
    discount += entry.sale.discount_sen ?? 0;

    for (const payment of entry.payments) {
      const method = payment.method as PaymentMethod;
      paymentBreakdown[method] = (paymentBreakdown[method] ?? 0) + payment.amount_sen;
      if (method === 'cash') {
        cashNet += payment.amount_sen - entry.sale.change_sen;
      }
    }
  }

  return {
    sales_count: salesCount,
    sales_total_sen: salesTotal,
    payment_breakdown: paymentBreakdown,
    cash_net_sen: cashNet,
    voided_count: voided,
    discount_sen: discount,
  };
}

export interface CloseShiftInput {
  shift: ShiftRecord;
  entries: readonly SaleWithPayments[];
  countedCashSen: number;
  now?: Date;
}

export interface ClosedShift {
  shift: ShiftRecord;
  summary: ShiftSummary;
}

export function closeShift(input: CloseShiftInput): ClosedShift {
  if (input.shift.status !== 'open') {
    throw new Error('Syif ini sudah ditutup');
  }
  if (!Number.isInteger(input.countedCashSen) || input.countedCashSen < 0) {
    throw new Error('Kiraan tunai mesti integer sen yang tidak negatif');
  }

  const summary = summarizeShift(input.entries);
  const expected = input.shift.opening_cash_sen + summary.cash_net_sen;
  const now = input.now ?? new Date();

  return {
    summary,
    shift: {
      ...input.shift,
      closed_at: now.toISOString(),
      counted_cash_sen: input.countedCashSen,
      expected_cash_sen: expected,
      variance_sen: input.countedCashSen - expected,
      sales_count: summary.sales_count,
      sales_total_sen: summary.sales_total_sen,
      payment_breakdown: summary.payment_breakdown,
      status: 'closed',
    },
  };
}

export function newShift(openingCashSen: number, now: Date = new Date()): ShiftRecord {
  if (!Number.isInteger(openingCashSen) || openingCashSen < 0) {
    throw new Error('Tunai awal mesti integer sen yang tidak negatif');
  }
  return {
    id: crypto.randomUUID(),
    opened_at: now.toISOString(),
    closed_at: null,
    opening_cash_sen: openingCashSen,
    counted_cash_sen: null,
    expected_cash_sen: null,
    variance_sen: null,
    sales_count: 0,
    sales_total_sen: 0,
    payment_breakdown: {},
    status: 'open',
  };
}
